import { useState } from 'react'
import type { Site, TimeEntry } from '@shared/types'
import { post } from '../lib/api'
import { fromLocalInput, toLocalInput } from '../lib/datetime'
import { Button, ErrorNote, Field, Sheet } from './ui'

/**
 * Asking the office to fix hours — a clock-in typed late after moving sites, a
 * clock-out that never happened, or a shift that was never clocked at all.
 * The crew no longer change their own times: they say what the times should
 * have been and why, the office gets a notification, and the change lands only
 * once somebody there approves it.
 */
export function RequestFixSheet({ entry, date, sites, timezone, onClose, onSent }: {
  /** Null = a missing shift on `date`. */
  entry: TimeEntry | null
  date: string
  sites: Site[]
  timezone: string
  onClose: () => void
  onSent: (message: string) => Promise<void> | void
}) {
  const wasOpen = entry?.clockOutAt === null
  const [siteId, setSiteId] = useState(entry?.siteId ?? sites[0]?.id ?? '')
  const [clockIn, setClockIn] = useState(() =>
    entry ? toLocalInput(entry.clockInAt, timezone) : `${date}T07:00`)
  const [clockOut, setClockOut] = useState(() =>
    entry?.clockOutAt ? toLocalInput(entry.clockOutAt, timezone) : entry ? '' : `${date}T15:30`)
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const send = async () => {
    if (!siteId) return setError('Pick the site.')
    if (!clockIn) return setError('The clock-in time is required.')
    if ((!entry || !wasOpen) && !clockOut) return setError('The clock-out time is required.')
    if (!reason.trim()) return setError('Say what went wrong — the office reads this first.')
    setBusy(true)
    setError(null)
    try {
      await post('/api/time/fix-requests', {
        entryId: entry?.id ?? null,
        siteId,
        clockInAt: fromLocalInput(clockIn, timezone),
        clockOutAt: clockOut ? fromLocalInput(clockOut, timezone) : null,
        reason: reason.trim(),
      })
      await onSent('Sent to the office')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send the request')
      setBusy(false)
    }
  }

  // An entry on a site that has since been archived is not in the active list.
  const siteOptions = entry && !sites.some((s) => s.id === entry.siteId)
    ? [{ id: entry.siteId, name: entry.siteName ?? 'Current site' }, ...sites]
    : sites

  return (
    <Sheet
      open
      onClose={onClose}
      title={entry ? 'Ask the office to fix this' : 'Add a missing shift'}
      footer={
        <Button variant="primary" size="lg" className="w-full" loading={busy} onClick={() => void send()}>
          Send to the office
        </Button>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-graphite-2">
          Put in what the times should be. Nothing changes until the office approves it.
        </p>
        <Field label="Site" required>
          <select className="field" value={siteId} onChange={(e) => setSiteId(e.target.value)}>
            {siteOptions.map((site) => (
              <option key={site.id} value={site.id}>{site.name}</option>
            ))}
          </select>
        </Field>
        <Field label="Clock in" required>
          <input
            className="field font-mono"
            type="datetime-local"
            value={clockIn}
            onChange={(e) => setClockIn(e.target.value)}
          />
        </Field>
        <Field
          label="Clock out"
          required={!wasOpen}
          hint={wasOpen ? 'Leave blank if you are still working.' : undefined}
        >
          <input
            className="field font-mono"
            type="datetime-local"
            value={clockOut}
            onChange={(e) => setClockOut(e.target.value)}
          />
        </Field>
        <Field label="What happened" required>
          <input
            className="field"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={entry ? 'Moved sites at 1:00 but forgot to switch' : 'Phone was dead all day'}
          />
        </Field>
        <ErrorNote>{error}</ErrorNote>
      </div>
    </Sheet>
  )
}
